import {
  createFeatureSelector,
  createReducer,
  createSelector,
  on,
} from '@ngrx/store';
import {
  AllowableCardTypes,
  PaymentInclusionExclusionRulesResponseV1,
  PaymentRemittanceRules,
  PaymentRulesResponseV1,
  PreAuthorizedDebitMode,
} from '@arc/unified-payments-frontend/shared-payment';
import { PaymentRulesActions } from '../actions';
import {
  HostedPaymentsPadResultComponentPadActions,
  HostedPaymentsPadResultComponentPaymentRulesActions,
  HostedPaymentsPaymentDetailsComponentActions,
  HostedPaymentsSelectPaymentMethodPaymentRulesActions,
  HostedPaymentsSessionComponentActions,
  MakePaymentCoreComponentActions,
  PayOnAccountCoreComponentActions,
  PoaPadResultComponentPadActions,
  PoaPadResultComponentPaymentRulesActions,
  PoaPaymentReceiptComponentActions,
  PoiIntlPaymentReceiptComponentActions,
  PoaSelectPaymentMethodPaymentRulesActions,
  PoiPadResultComponentPadActions,
  PoiPadResultComponentPaymentRulesActions,
  PoiPaymentReceiptComponentActions,
  PoiSelectPaymentMethodPaymentRulesActions,
  PoiStripeResultIntlPaymentRulesActions,
} from '../../component-actions';

export interface PaymentRulesState {
  paymentRule?: PaymentRulesResponseV1;
  paymentInclusionExclusion?: PaymentInclusionExclusionRulesResponseV1;
  paymentRemittanceRules?: PaymentRemittanceRules;
  preAuthorizedDebitMode?: PreAuthorizedDebitMode;
}

export const defaultAllowableCardBrands: AllowableCardTypes[] = [
  'visa',
  'american-express',
  'mastercard',
  'discover',
];

const initialState: PaymentRulesState = {};

export const paymentRulesReducers = createReducer(
  initialState,
  on(PaymentRulesActions.SetPaymentRuleToNullAction, (): PaymentRulesState => {
    return {
      paymentRule: null,
    };
  }),
  on(
    PaymentRulesActions.GetPaymentRuleCompleteAction,
    HostedPaymentsSessionComponentActions.loadPaymentRulesSuccess,
    PoaPadResultComponentPaymentRulesActions.loadPaymentRulesSuccess,
    PoiPadResultComponentPaymentRulesActions.loadPaymentRulesSuccess,
    PoiStripeResultIntlPaymentRulesActions.loadPaymentRulesSuccess,
    HostedPaymentsPadResultComponentPaymentRulesActions.loadPaymentRulesSuccess,
    PoiPaymentReceiptComponentActions.loadPaymentRulesSuccess,
    PoaPaymentReceiptComponentActions.loadPaymentRulesSuccess,
    PoiIntlPaymentReceiptComponentActions.loadPaymentRulesSuccess,
    (state, action): PaymentRulesState => {
      const newPaymentRules = { ...action.payload };
      const paymentsSettings = action.payload?.settings?.payments;
      if (!(paymentsSettings.allowableCardBrands?.length > 0)) {
        newPaymentRules.settings.payments.allowableCardBrands =
          defaultAllowableCardBrands;
      } else {
        newPaymentRules.settings.payments.allowableCardBrands =
          paymentsSettings.allowableCardBrands.map((c) =>
            c.replace(/\s+/g, '-').toLowerCase(),
          ) as AllowableCardTypes[];
      }
      return {
        ...state,
        paymentRule: newPaymentRules,
      };
    },
  ),
  on(
    PoiSelectPaymentMethodPaymentRulesActions.inclusionExclusionListUpdate,
    PoiPadResultComponentPaymentRulesActions.loadInclusionExclusionRulesSuccess,
    PoaSelectPaymentMethodPaymentRulesActions.inclusionExclusionListUpdate,
    PoaPadResultComponentPaymentRulesActions.loadInclusionExclusionRulesSuccess,
    HostedPaymentsSelectPaymentMethodPaymentRulesActions.cardInclusionExclusionListUpdated,
    HostedPaymentsPadResultComponentPaymentRulesActions.loadInclusionExclusionRulesSuccess,
    (state, action): PaymentRulesState => {
      return {
        ...state,
        paymentInclusionExclusion: action.payload,
      };
    },
  ),
  on(
    PaymentRulesActions.GetPaymentRemittanceRulesCompleteAction,
    PoaPadResultComponentPaymentRulesActions.getPaymentRemittanceRulesSuccess,
    (state, action): PaymentRulesState => {
      return {
        ...state,
        paymentRemittanceRules: action.payload,
      };
    },
  ),
  on(
    HostedPaymentsPaymentDetailsComponentActions.accountNumberChanged,
    (state): PaymentRulesState => {
      return {
        ...state,
        paymentInclusionExclusion: null,
      };
    },
  ),
  on(
    HostedPaymentsSessionComponentActions.loadPreAuthorizedDebitModeSuccess,
    MakePaymentCoreComponentActions.loadPreAuthorizedDebitModeSuccess,
    PayOnAccountCoreComponentActions.loadPreAuthorizedDebitModeSuccess,
    PoaPadResultComponentPadActions.loadPreAuthorizedDebitModeSuccess,
    HostedPaymentsPadResultComponentPadActions.loadPreAuthorizedDebitModeSuccess,
    PoiPadResultComponentPadActions.loadPreAuthorizedDebitModeSuccess,
    (state, action): PaymentRulesState => {
      return {
        ...state,
        preAuthorizedDebitMode: action.payload,
      };
    },
  ),
);

export const PayRuleStore =
  createFeatureSelector<PaymentRulesState>('paymentRules');

export const paymentRuleMap = (
  state: PaymentRulesState,
): PaymentRulesResponseV1 => state.paymentRule;

export const PaymentRuleStore = createSelector(PayRuleStore, paymentRuleMap);
